import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Construction,
  Wrench,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  Activity,
  Ruler,
  ExternalLink,
  Home,
  Building2,
  CircleDot,
  ShieldCheck,
  HelpCircle,
  Droplets,
  Syringe,
  FlaskConical,
  BarChart3,
  TrendingUp,
  Mountain,
  Factory,
  Waves,
  BookOpen
} from 'lucide-react';

const InjectionGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Qu'est-ce que l'injection ?" },
    { id: 'section-2', title: "Les familles d'injection par objectif" },
    { id: 'section-3', title: "Les mécanismes d'injection" },
    { id: 'section-4', title: "Les produits d'injection" },
    { id: 'section-5', title: "Focus : résine expansive" },
    { id: 'section-6', title: "Les étapes d'un chantier" },
    { id: 'section-7', title: "Contrôle et traçabilité" },
    { id: 'section-8', title: "Questions fréquentes" },
  ];

  const faqs = [
    {
      question: "Quelle norme encadre les travaux d'injection ?",
      answer: "La norme NF EN 12715 (Exécution des travaux géotechniques spéciaux — Injections). Elle couvre les injections avec et sans déplacement des terrains, et précise les prescriptions sur les matériaux, l'exécution, les essais et les contrôles. Le jet-grouting relève de la NF EN 12716."
    },
    {
      question: "Comment choisit-on le coulis ?",
      answer: "Principalement selon la granulométrie du terrain : le coulis doit pouvoir pénétrer les vides. Ciment classique pour les graviers et sables grossiers, ciment ultrafin ou coulis chimique pour les sables fins, claquage ou résine pour les sols argileux peu perméables."
    },
    {
      question: "Qu'est-ce qu'un tube à manchettes ?",
      answer: "C'est un tube perforé équipé de manchettes en caoutchouc, scellé dans le forage. Il permet d'injecter sélectivement à la profondeur souhaitée et de répéter l'injection plusieurs fois au même point — c'est ce qui rend possible les injections répétitives et sélectives, et les injections de compensation."
    },
    {
      question: "L'injection peut-elle soulever un bâtiment ?",
      answer: "Oui, et c'est parfois l'objectif recherché (relevage par résine expansive, injection de compensation). Mais un soulèvement non maîtrisé est un risque majeur : il faut piloter la pression et suivre le nivellement en continu."
    },
    {
      question: "Peut-on injecter sous une maison habitée ?",
      answer: "Oui. C'est même l'un des atouts de la résine expansive et de l'injection par petits forages : l'intervention est peu invasive et les délais courts comparés à une reprise en sous-œuvre lourde. Le choix de la technique dépend du diagnostic."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Injection en géotechnique : coulis, traitement de sol et étapes | Guide complet",
    "description": "Injection de coulis de ciment, comblement, traitement de sol, résine expansive : mécanismes, produits, étapes de chantier et contrôle selon la norme NF EN 12715.",
    "image": "https://geots.consulting/og-images/guide-injection.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-19",
    "dateModified": "2026-07-19"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Injection families by objective
  const injectionFamilies = [
    { 
      objective: "Comblement", 
      goal: "Remplir un vide existant", 
      cases: "Cavités, anciennes carrières, karsts, conduites abandonnées",
      icon: Layers,
      color: "bg-blue-50 border-blue-200"
    },
    { 
      objective: "Consolidation", 
      goal: "Renforcer, réduire la déformabilité", 
      cases: "Confortement de fondations, sols granulaires lâches",
      icon: ShieldCheck,
      color: "bg-green-50 border-green-200"
    },
    { 
      objective: "Étanchéité", 
      goal: "Réduire la perméabilité", 
      cases: "Voiles d'étanchéité, barrages, fouilles sous nappe",
      icon: Droplets,
      color: "bg-cyan-50 border-cyan-200"
    },
    { 
      objective: "Scellement", 
      goal: "Ancrer un élément dans le terrain", 
      cases: "Micropieux, tirants d'ancrage, boulonnage rocheux",
      icon: CircleDot,
      color: "bg-purple-50 border-purple-200"
    },
    { 
      objective: "Relevage / Résine", 
      goal: "Compacter et soulever de façon contrôlée", 
      cases: "Dallages affaissés, semelles, tassements différentiels",
      icon: TrendingUp,
      color: "bg-orange-50 border-orange-200"
    },
  ];

  // Injection mechanisms
  const mechanisms = [
    { 
      name: "Imprégnation", 
      desc: "Le coulis remplit les vides interstitiels du sol sans altérer sa structure. On obtient une masse cimentée plus résistante et moins perméable.",
      terrain: "Sols granulaires (sables, graviers), rocher fissuré",
      icon: Waves,
      color: "bg-blue-100 border-blue-300"
    },
    { 
      name: "Claquage (fracturation)", 
      desc: "Injection à pression suffisante pour créer de petites fractures qui se remplissent de coulis. Le terrain est parcouru d'un réseau de lamelles durcies.",
      terrain: "Sols fins peu perméables (argiles, limons)",
      icon: Activity,
      color: "bg-amber-100 border-amber-300"
    },
    { 
      name: "Compactage (injection solide)", 
      desc: "Un mortier très consistant forme un bulbe qui écarte et densifie le terrain alentour. L'amélioration vient de la compaction, pas du remplissage.",
      terrain: "Sols lâches à densifier",
      icon: Target,
      color: "bg-green-100 border-green-300"
    },
  ];

  // Injection products
  const products = [
    { name: "Coulis de ciment", desc: "Le produit de référence. Coulis instables (gros vides) ou stables (imprégnation fine). Ciment ultrafin pour sols plus serrés.", icon: FlaskConical },
    { name: "Coulis bentonite-ciment", desc: "Meilleure stabilité et prise adaptée. Utilisés notamment pour l'étanchéité.", icon: Droplets },
    { name: "Mortiers", desc: "Pour le comblement de gros volumes et l'injection solide de compactage.", icon: Layers },
    { name: "Coulis chimiques", desc: "Silicates, résines : quand la granulométrie du terrain est trop fine pour un liant particulaire.", icon: FlaskConical },
    { name: "Résines expansives", desc: "Polyuréthane bi-composant, expansion contrôlée. Très utilisées pour le relevage de bâtiments.", icon: TrendingUp },
  ];

  // Chantier steps
  const chantierSteps = [
    { number: 1, title: "Reconnaissance géotechnique", desc: "Sondages, essais de perméabilité, géophysique. Connaître la nature du terrain, sa perméabilité, localiser les vides.", icon: Mountain },
    { number: 2, title: "Conception du traitement", desc: "Maillage des forages, profondeurs, volumes prévisionnels, choix du coulis, pressions et critères d'arrêt.", icon: FileText },
    { number: 3, title: "Forage", desc: "Forages de petit diamètre (50 à 80 mm), verticaux, inclinés ou sub-horizontaux selon la configuration.", icon: Cog },
    { number: 4, title: "Équipement du forage", desc: "À trou ouvert avec obturateur, ou avec tube à manchettes (TAM) pour injections répétitives et sélectives.", icon: Settings },
    { number: 5, title: "Injection par passes", desc: "Injection niveau par niveau en suivant en continu la pression, le débit et le volume.", icon: Gauge },
    { number: 6, title: "Critères d'arrêt", desc: "Volume prévu absorbé, pression de refus atteinte, ou débit devenu nul.", icon: AlertTriangle },
    { number: 7, title: "Contrôles a posteriori", desc: "Essais de perméabilité, forages de reconnaissance, essais pressiométriques, mesures de nivellement.", icon: CheckCircle2 },
  ];

  return (
    <>
      <Helmet>
        <title>Injection en géotechnique : coulis, traitement de sol et étapes | Guide complet</title>
        <meta name="description" content="Injection de coulis de ciment, comblement, traitement de sol, résine expansive : mécanismes, produits, étapes de chantier et contrôle selon la norme NF EN 12715." />
        <link rel="canonical" href="https://geots.consulting/guides/injection-coulis-traitement-de-sol" />
        <meta property="og:title" content="Injection en géotechnique : coulis, traitement de sol et étapes | Guide complet" />
        <meta property="og:description" content="Injection de coulis de ciment, comblement, traitement de sol, résine expansive : mécanismes, produits, étapes de chantier et contrôle selon la norme NF EN 12715." />
        <meta property="og:url" content="https://geots.consulting/guides/injection-coulis-traitement-de-sol" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
          <div className="container mx-auto px-4 py-12 md:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8 text-gray-300">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-white">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Injection géotechnique</span>
            </nav>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-[#635BFF]/20 text-[#00D4FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Syringe className="w-4 h-4" />
                Guide technique
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                L'injection en géotechnique : guide complet
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D4FF] to-[#635BFF] mt-2">
                  (coulis, mécanismes et étapes de chantier)
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
                Combler une cavité, renforcer un sol trop meuble, étanchéifier un terrain perméable, sceller un micropieu, 
                relever un dallage affaissé : derrière ces objectifs très différents se cache une même famille de techniques, 
                l'injection. Coulis de ciment, mortiers, résines expansives — comment ça marche, et comment se déroule un chantier ?
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Mis à jour : Juillet 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Temps de lecture : 12 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Sommaire
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Related Guides */}
                <div className="bg-gradient-to-br from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mt-6 border border-[#635BFF]/10">
                  <h4 className="font-bold text-[#0A2540] mb-4">Guides connexes</h4>
                  <div className="space-y-3">
                    <Link to="/guides/comment-choisir-fondations-speciales" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Choisir ses fondations spéciales
                    </Link>
                    <Link to="/guides/micropieux" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Micropieux : guide complet
                    </Link>
                    <Link to="/guides/fissures-maison-quand-s-inquieter" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Fissures maison : quand s'inquiéter ?
                    </Link>
                    <Link to="/guides/enregistreur-parametres-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Enregistreur de paramètres de forage
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Article */}
            <article className="flex-1 max-w-3xl">
              {/* Section 1: What is injection? */}
              <section id="section-1" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <Syringe className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Qu'est-ce que l'injection en géotechnique ?
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    L'injection consiste à <strong>introduire un produit fluide</strong> — coulis de ciment, mortier, résine — 
                    dans un sol, une roche fissurée ou un vide, <strong>sous pression contrôlée</strong>, afin d'en modifier 
                    les propriétés ou de le combler.
                  </p>

                  <p>
                    C'est une technique de <strong>travaux géotechniques spéciaux</strong>, encadrée en Europe par la norme 
                    <strong> NF EN 12715</strong> (Exécution des travaux géotechniques spéciaux — Injections).
                  </p>

                  <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 my-6 border-l-4 border-[#635BFF]">
                    <p className="font-medium text-[#0A2540] mb-2 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-[#635BFF]" />
                      Distinction fondamentale
                    </p>
                    <p className="text-gray-700 mb-0">
                      La norme distingue deux grandes catégories : les injections <strong>sans déplacement des terrains</strong> 
                      (imprégnation, comblement) et les injections <strong>avec déplacement des terrains</strong> 
                      (injection solide, fracturation hydraulique). Selon qu'on remplit les vides ou qu'on écarte le terrain, 
                      le comportement, les risques et les contrôles ne sont pas les mêmes.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2: Injection families by objective */}
              <section id="section-2" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-blue-600" />
                  </span>
                  Les grandes familles d'injection, classées par objectif
                </h2>

                <p className="text-gray-700 mb-6">
                  Un même chantier peut combiner plusieurs objectifs — par exemple combler des vides tout en renforçant le sol environnant.
                </p>

                <div className="space-y-4 mb-8">
                  {injectionFamilies.map((family, index) => {
                    const IconComponent = family.icon;
                    return (
                      <div 
                        key={index}
                        className={`rounded-xl p-5 border-2 ${family.color}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                            <IconComponent className="w-6 h-6 text-[#0A2540]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-[#0A2540] mb-1">{family.objective}</h3>
                            <p className="text-gray-700 text-sm mb-2">{family.goal}</p>
                            <p className="text-gray-500 text-sm italic">{family.cases}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 3: Mechanisms */}
              <section id="section-3" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Cog className="w-5 h-5 text-green-600" />
                  </span>
                  Les mécanismes d'injection
                </h2>

                <p className="text-gray-700 mb-6">
                  C'est le cœur technique du sujet. <strong>Le mécanisme dépend du terrain</strong>, en particulier de sa perméabilité et de sa granulométrie.
                </p>

                <div className="space-y-6 mb-8">
                  {mechanisms.map((mech, index) => {
                    const IconComponent = mech.icon;
                    return (
                      <div 
                        key={index}
                        className={`rounded-xl p-6 border-2 ${mech.color}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                            <IconComponent className="w-7 h-7 text-[#0A2540]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-[#0A2540] text-lg mb-2">{mech.name}</h3>
                            <p className="text-gray-700 mb-3">{mech.desc}</p>
                            <div className="bg-white/50 rounded-lg px-4 py-2 inline-block">
                              <span className="text-sm text-gray-600">
                                <strong>Terrain d'élection :</strong> {mech.terrain}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-2">Condition impérative pour l'imprégnation</h4>
                      <p className="text-amber-700">
                        La <strong>taille des particules du coulis doit être adaptée à la taille des vides</strong>. 
                        Un ciment classique ne pénètre pas un sable fin — on utilisera alors un ciment ultrafin ou un coulis chimique. 
                        C'est la première règle : si le coulis ne passe pas, il ne sert à rien.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-gray-50 rounded-xl p-6 border">
                  <h4 className="font-bold text-[#0A2540] mb-3">Et le jet-grouting ?</h4>
                  <p className="text-gray-700">
                    Le jet-grouting ne relève pas à proprement parler de l'injection classique : un jet à très haute pression 
                    <strong> déstructure le terrain</strong> et le mélange au liant pour créer une colonne sol-ciment. 
                    Il fait l'objet d'une norme dédiée (<strong>NF EN 12716</strong>).
                  </p>
                  <Link 
                    to="/guides/comment-choisir-fondations-speciales"
                    className="inline-flex items-center gap-2 mt-3 text-[#635BFF] font-medium hover:underline"
                  >
                    Voir notre guide sur les fondations spéciales
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </section>

              {/* Section 4: Products */}
              <section id="section-4" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <FlaskConical className="w-5 h-5 text-purple-600" />
                  </span>
                  Les produits d'injection
                </h2>

                <p className="text-gray-700 mb-6">
                  Le choix se fait sur trois critères : la <strong>granulométrie du terrain</strong>, 
                  la <strong>viscosité et le temps de prise</strong> du produit, et l'<strong>objectif</strong> (remplir ? renforcer ? étanchéifier ?).
                </p>

                <div className="grid gap-4">
                  {products.map((product, index) => {
                    const IconComponent = product.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-5 border hover:border-[#635BFF]/50 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-1">{product.name}</h3>
                            <p className="text-gray-600 text-sm">{product.desc}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 5: Expansive resin focus */}
              <section id="section-5" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </span>
                  Focus : l'injection de résine expansive
                </h2>

                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-200 mb-6">
                  <h3 className="font-bold text-[#0A2540] text-lg mb-4">Une technique en fort développement</h3>
                  <p className="text-gray-700 mb-4">
                    Une résine polyuréthane <strong>bi-composant</strong> est injectée dans le sol à travers de petits tubes. 
                    En réagissant, elle <strong>expanse</strong> avec une forte pression de gonflement. Au sens de la norme NF EN 12715, 
                    elle relève des injections avec déplacement des terrains.
                  </p>
                  
                  <div className="bg-white rounded-xl p-4 mb-4">
                    <h4 className="font-bold text-[#0A2540] mb-2">Le triple effet :</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Comblement</strong> des vides</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Pénétration</strong> y compris dans les sols peu perméables (argiles)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Densification</strong> du terrain, améliorant sa capacité portante</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <h3 className="font-bold text-[#0A2540] text-lg mb-4">Les applications principales</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 border">
                    <Home className="w-6 h-6 text-[#635BFF] mb-2" />
                    <h4 className="font-semibold text-[#0A2540] mb-1">Dallages affaissés</h4>
                    <p className="text-sm text-gray-600">Relevage et stabilisation</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border">
                    <Building2 className="w-6 h-6 text-[#635BFF] mb-2" />
                    <h4 className="font-semibold text-[#0A2540] mb-1">Semelles de fondation</h4>
                    <p className="text-sm text-gray-600">Confortement sur maison fissurée</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border">
                    <Layers className="w-6 h-6 text-[#635BFF] mb-2" />
                    <h4 className="font-semibold text-[#0A2540] mb-1">Vides sous dalle</h4>
                    <p className="text-sm text-gray-600">Comblement et stabilisation</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border">
                    <Activity className="w-6 h-6 text-[#635BFF] mb-2" />
                    <h4 className="font-semibold text-[#0A2540] mb-1">Tassements différentiels</h4>
                    <p className="text-sm text-gray-600">Traitement ciblé</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#635BFF]">
                  <h4 className="font-bold text-[#0A2540] mb-3">Résine ou micropieux ?</h4>
                  <p className="text-gray-700 mb-4">
                    La résine est plus rapide, peu invasive (petits forages), et permet un relevage millimétrique. 
                    Les micropieux vont chercher un ancrage profond et reprennent des charges plus élevées. 
                    Le choix dépend entièrement du <strong>diagnostic géotechnique</strong> : profondeur du bon sol, nature des désordres, charges à reprendre.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link 
                      to="/guides/micropieux"
                      className="inline-flex items-center gap-2 text-sm text-[#635BFF] font-medium hover:underline"
                    >
                      Guide micropieux <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link 
                      to="/guides/fissures-maison-quand-s-inquieter"
                      className="inline-flex items-center gap-2 text-sm text-[#635BFF] font-medium hover:underline"
                    >
                      Guide fissures maison <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Section 6: Chantier steps */}
              <section id="section-6" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                    <Construction className="w-5 h-5 text-cyan-600" />
                  </span>
                  Les étapes d'un chantier d'injection
                </h2>

                <div className="space-y-6">
                  {chantierSteps.map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                      <div 
                        key={index}
                        className="flex items-start gap-4 bg-white rounded-xl p-6 border shadow-sm"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {step.number}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                            <h3 className="font-bold text-[#0A2540]">{step.title}</h3>
                          </div>
                          <p className="text-gray-600">{step.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Le tube à manchettes (TAM)
                  </h4>
                  <p className="text-blue-700">
                    Un tube perforé équipé de manchettes en caoutchouc est scellé dans le forage par une gaine. 
                    Il permet d'injecter <strong>à volonté, à la profondeur voulue, et de revenir plusieurs fois au même point</strong> 
                    (injections répétitives et sélectives). C'est la solution la plus fine et la plus contrôlée.
                  </p>
                </div>
              </section>

              {/* Section 7: Control */}
              <section id="section-7" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-red-600" />
                  </span>
                  Le contrôle et la traçabilité : le point critique
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700 mb-6">
                  <p>
                    Une injection est par nature <strong>invisible</strong> : le produit part dans le sol, et personne ne peut 
                    aller vérifier ce qui s'est réellement passé. La seule preuve d'exécution, ce sont les <strong>paramètres enregistrés</strong>.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border shadow-sm mb-6">
                  <h4 className="font-bold text-[#0A2540] mb-4">Pour chaque passe injectée, il faut tracer :</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                      <Gauge className="w-5 h-5 text-[#635BFF]" />
                      <span className="text-gray-700">La <strong>pression</strong> d'injection</span>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                      <Activity className="w-5 h-5 text-[#635BFF]" />
                      <span className="text-gray-700">Le <strong>débit</strong></span>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                      <FlaskConical className="w-5 h-5 text-[#635BFF]" />
                      <span className="text-gray-700">Le <strong>volume</strong> de coulis absorbé</span>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                      <Ruler className="w-5 h-5 text-[#635BFF]" />
                      <span className="text-gray-700">La <strong>profondeur</strong> et la durée</span>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-2">Le risque à maîtriser</h4>
                      <p className="text-red-700">
                        Injecter à une pression trop forte par rapport à la profondeur peut provoquer un <strong>claquage non désiré</strong> 
                        et un <strong>soulèvement du terrain</strong> — ce qui peut endommager les ouvrages voisins. 
                        D'où l'importance du suivi en continu et du nivellement.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cross-link */}
                <div className="mt-6">
                  <Link 
                    to="/guides/enregistreur-parametres-forage"
                    className="flex items-center gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-colors"
                  >
                    <BarChart3 className="w-6 h-6 text-indigo-600" />
                    <div>
                      <p className="font-semibold text-indigo-800">Enregistreur de paramètres</p>
                      <p className="text-sm text-indigo-600">Guide complet sur l'instrumentation</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-indigo-600 ml-auto" />
                  </Link>
                </div>
              </section>

              {/* Section 8: FAQ */}
              <section id="section-8" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-indigo-600" />
                  </span>
                  Questions fréquentes
                </h2>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0A2540] pr-4">{faq.question}</span>
                        {openFaq === index ? (
                          <ChevronUp className="w-5 h-5 text-[#635BFF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-5 text-gray-600 border-t">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* CTA Box */}
              <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-8 text-white mb-8">
                <h3 className="text-xl font-bold mb-4">
                  Un projet d'injection à cadrer ?
                </h3>
                <p className="text-gray-300 mb-6">
                  GEOTS met en relation les acteurs de la géotechnique et des fondations spéciales : entreprises d'injection 
                  et de traitement de sol, bureaux d'études, loueurs de matériel de forage, d'injection et d'instrumentation.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248d9] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Contactez-nous
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link 
                    to="/annuaire/travaux-fondations-speciales/injections-jet-grouting"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Voir l'annuaire injections
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="text-sm text-gray-500 italic border-t pt-8">
                Ce guide est fourni à titre informatif. La conception et la réalisation de travaux d'injection doivent s'appuyer 
                sur une reconnaissance géotechnique et l'intervention d'un bureau d'études et d'une entreprise spécialisés, 
                conformément à la norme NF EN 12715.
              </div>
            </article>
          </div>
        </div>

        {/* Related Guides Section */}
        <div className="bg-gray-50 border-t py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-8 text-center">
              Continuez votre lecture
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link 
                to="/guides/comment-choisir-fondations-speciales"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Comment choisir ses fondations spéciales ?
                </h3>
                <p className="text-gray-600 text-sm">
                  Pieux, micropieux, parois moulées, jet-grouting.
                </p>
              </Link>

              <Link 
                to="/guides/micropieux"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center mb-4">
                  <CircleDot className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Micropieux : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Types I à IV, mise en œuvre et reprise en sous-œuvre.
                </p>
              </Link>

              <Link 
                to="/guides/fissures-maison-quand-s-inquieter"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <Home className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Fissures maison : quand s'inquiéter ?
                </h3>
                <p className="text-gray-600 text-sm">
                  Types, causes, solutions et assurance.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InjectionGuide;
